/**
 * Dashboard Executivo — Monitoramento de Dispositivos.
 *
 * Fonte: GET /device-monitoring/dashboard/executive (agregado no backend;
 * classificação/transição/reincidência vêm da camada única de inteligência).
 * Todos os blocos obedecem aos mesmos filtros globais; clicar em barras e
 * linhas abre o drill-down com EXATAMENTE os registros contados.
 * Unidades nunca se misturam: eventos (porta-isca), roedores (T-Rex),
 * insetos (luminosa), gramas (biológica).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage, typeLabel } from "./hierarchy";
import {
  AXIS, CLASS_INFO, Card, ChartTooltip, Delta, DeviceDetail, DrillDown, Empty,
  GRID, GlobalFilters, NEUTRAL, RISK_INFO, STATUS, Stat, TRANSITION_INFO, filtersToParams,
  fmtDay, useCategorical,
} from "./viz";

const GAP = { stroke: "hsl(var(--card))", strokeWidth: 2 }; // 2px de superfície entre segmentos
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("pt-BR")}%`);

function HBar({ data, dataKey, nameKey, unit, color, onClick, height }) {
  return (
    <ResponsiveContainer width="100%" height={height || Math.max(160, data.length * 34 + 30)}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 48, top: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} stroke={GRID} />
        <XAxis type="number" tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey={nameKey} width={130} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip content={<ChartTooltip unit={unit ? ` ${unit}` : ""} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
        <Bar dataKey={dataKey} name={unit || dataKey} fill={color} radius={[0, 4, 4, 0]} maxBarSize={18}
          onClick={onClick} className={onClick ? "cursor-pointer" : ""}>
          <LabelList dataKey={dataKey} position="right" fill={AXIS} fontSize={11} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function Table({ cols, rows, empty }) {
  if (!rows?.length) return <Empty text={empty} />;
  return (
    <div className="overflow-auto max-h-80">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>{cols.map((c) => <th key={c.key} className={`py-1.5 px-2 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.device_id || r.area_id || i} className="border-t border-border/50">
              {cols.map((c) => <td key={c.key} className={`py-1.5 px-2 ${c.right ? "text-right" : ""}`}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DeviceMonitoringDashboard({ filters, setFilters }) {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [device, setDevice] = useState(null);
  const [sectorMode, setSectorMode] = useState("qtd");

  useEffect(() => {
    if (filters.period === "personalizado" && (!filters.date_from || !filters.date_to)) return;
    let current = true;
    setLoading(true);
    const params = filtersToParams(filters);
    Promise.all([
      api.get("/device-monitoring/dashboard/executive", { params }),
      api.get("/device-monitoring/analytics/overview", { params }),
      api.get("/device-monitoring/alerts", { params: { status: "ABERTO", limit: 8, client_id: filters.client_id || undefined } }),
    ]).then(([d, a, al]) => {
      if (!current) return;
      setData(d.data);
      setInsights(a.data.insights || []);
      setAlerts(al.data.items || []);
    }).catch((err) => current && toast.error(apiErrorMessage(err, "Erro ao carregar o dashboard.")))
      .finally(() => current && setLoading(false));
    return () => { current = false; };
  }, [filters]);

  const closeDrill = useCallback(() => setDrill(null), []);
  const closeDevice = useCallback(() => setDevice(null), []);
  const openDevice = useCallback((id) => { setDrill(null); setDevice(id); }, []);

  const speciesKeys = useMemo(() => {
    const all = [...(data?.trex?.by_species || []), ...(data?.luminous?.by_species || [])];
    return [...new Set(all.map((s) => s.species_id))];
  }, [data]);
  const speciesColor = useCategorical(speciesKeys);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <GlobalFilters value={filters} onChange={setFilters} />
        <div className="py-16 text-center text-muted-foreground">Carregando…</div>
      </div>
    );
  }
  const k = data?.kpis || {};
  const t = data?.consumption_trend;
  const sectorRows = (data?.consumption_by_sector || []).map((s) => ({
    ...s, partial_v: sectorMode === "pct" ? +(s.partial / Math.max(s.total, 1) * s.pct).toFixed(1) : s.partial,
    consumed_v: sectorMode === "pct" ? +(s.consumed / Math.max(s.total, 1) * s.pct).toFixed(1) : s.consumed,
  }));

  return (
    <div className="space-y-4" data-testid="dm-executive-dashboard">
      <GlobalFilters value={filters} onChange={setFilters} />
      {data && !data.has_data && (
        <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
          Sem inspeções no período/filtros selecionados ({fmtDay(data.period.from)} – {fmtDay(data.period.to)}).
        </div>
      )}

      {/* LINHA 1 — KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dm-kpis">
        <Stat label="Dispositivos ativos" value={k.active_devices} />
        <Stat label="Previstos" value={k.expected} hint="cronogramas do período" />
        <Stat label="Executados" value={k.executed} />
        <Stat label="Pendentes" value={k.pending} />
        <Stat label="% de cumprimento" value={pct(k.compliance_pct)} />
        <Stat label="Normal" value={k.normal} accent={STATUS.good} />
        <Stat label="Atenção" value={k.attention} accent={STATUS.warning} />
        <Stat label="Crítico" value={k.critical} accent={STATUS.critical}
          hint={k.critical_operational ? `${k.critical_operational} operacional` : undefined} />
        <Stat label="Com atividade" value={k.devices_with_activity} />
        <Stat label="Reincidentes" value={k.recurrent_devices}
          hint={k.needs_review ? `${k.needs_review} para revisar status` : undefined} />
      </div>

      {/* LINHA 2 — cumprimento + classificação */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Cumprimento do cronograma" subtitle="Executados = MonitoringRecords persistidos">
          {!k.expected ? <Empty text="Sem cronogramas no período" /> : (
            <div className="space-y-2">
              <div className="text-3xl font-semibold">{pct(k.compliance_pct)}</div>
              <div className="h-3 rounded bg-secondary overflow-hidden">
                <div className="h-full" style={{ width: `${k.compliance_pct || 0}%`, background: STATUS.good }} />
              </div>
              <div className="text-xs text-muted-foreground">{k.executed} de {k.expected} executados · {k.pending} pendentes</div>
            </div>
          )}
        </Card>
        <Card title="Classificação operacional" subtitle="Última inspeção de cada dispositivo no período (clique para detalhar)">
          {!k.monitored_devices ? <Empty /> : (
            <div className="space-y-1.5">
              {(data.classification || []).filter((c) => c.count > 0 || c.class !== "NEEDS_REVIEW").map((c) => {
                const info = CLASS_INFO[c.class] || {};
                const share = k.monitored_devices ? (c.count / k.monitored_devices) * 100 : 0;
                return (
                  <button key={c.class} className="w-full text-left group" onClick={() => c.count && setDrill({
                    title: `${info.label} — última inspeção por dispositivo`,
                    params: { metric: "all", classification: c.class, latest_only: true } })}>
                    <div className="flex items-center gap-2 text-sm">
                      <span aria-hidden style={{ color: info.color }}>{info.icon}</span>
                      <span>{info.label}</span>
                      <span className="ml-auto font-medium">{c.count}</span>
                      <span className="w-12 text-right text-xs text-muted-foreground">{share.toFixed(0)}%</span>
                    </div>
                    <div className="h-2 rounded bg-secondary overflow-hidden">
                      <div className="h-full rounded-r" style={{ width: `${share}%`, background: info.color }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* LINHA 3 — consumo por setor + tendência */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Consumo por setor — porta-iscas" subtitle="Eventos parcialmente consumido + consumido (intacto não é consumo)"
          right={
            <div className="flex text-xs border border-border rounded-md overflow-hidden">
              {[["qtd", "Quantidade"], ["pct", "%"]].map(([v, l]) => (
                <button key={v} onClick={() => setSectorMode(v)}
                  className={`px-2 py-1 ${sectorMode === v ? "bg-secondary font-medium" : "text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
          }>
          {!sectorRows.length ? <Empty text="Nenhum consumo no período" /> : (
            <ResponsiveContainer width="100%" height={Math.max(180, sectorRows.length * 40 + 50)}>
              <BarChart data={sectorRows} layout="vertical" margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
                <CartesianGrid horizontal={false} stroke={GRID} />
                <XAxis type="number" tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false}
                  unit={sectorMode === "pct" ? "%" : ""} allowDecimals={sectorMode === "pct"} />
                <YAxis type="category" dataKey="area_name" width={120} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip unit={sectorMode === "pct" ? "%" : ""} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="partial_v" name="Parcialmente consumido" stackId="c" fill={STATUS.warning} {...GAP} maxBarSize={22}
                  className="cursor-pointer" onClick={(evt) => { const row = evt?.payload || evt; return setDrill({ title: `${row.area_name} — parcialmente consumido`,
                    params: { metric: "consumption_partial", area_id: row.area_id } }); }} />
                <Bar dataKey="consumed_v" name="Consumido" stackId="c" fill={STATUS.critical} {...GAP} radius={[0, 4, 4, 0]} maxBarSize={22}
                  className="cursor-pointer" onClick={(evt) => { const row = evt?.payload || evt; return setDrill({ title: `${row.area_name} — consumido`,
                    params: { metric: "consumption_total", area_id: row.area_id } }); }}>
                  <LabelList dataKey={sectorMode === "pct" ? "pct" : "total"} position="right" fill={AXIS} fontSize={11}
                    formatter={(v) => (sectorMode === "pct" ? `${v}%` : v)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Tendência de consumo" subtitle={`Por ${t?.granularity || "dia"} · porta-iscas`}
          right={<Delta cmp={t?.comparison} />}>
          {!t?.series?.some((s) => s.partial || s.consumed) ? <Empty text="Nenhum consumo no período" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={t.series} margin={{ left: -16, right: 12, top: 8, bottom: 0 }}
                onClick={(e) => e?.activeLabel && setDrill({ title: `Consumos em ${fmtDay(e.activeLabel)}`,
                  params: { metric: "consumption", bucket: e.activeLabel } })}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="bucket" tickFormatter={fmtDay} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} labelFormatter={fmtDay} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="partial" name="Parcial" stroke={STATUS.warning} strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="consumed" name="Consumido" stroke={STATUS.critical} strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="devices_with_consumption" name="Dispositivos com consumo" stroke={NEUTRAL} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* LINHA 4 — reincidentes + pressão por área + top consumo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top dispositivos reincidentes" subtitle="Atividade em ≥ 3 das últimas 5 inspeções">
          <Table rows={data?.top_recurrent} empty="Nenhum dispositivo reincidente" cols={[
            { key: "code", label: "Dispositivo", render: (r) => <button className="underline font-mono" onClick={() => setDevice(r.device_id)}>{r.code}</button> },
            { key: "area_name", label: "Setor" },
            { key: "events", label: "Eventos", right: true, render: (r) => `${r.events_in_window}/${r.window}` },
            { key: "consecutive", label: "Seq.", right: true },
            { key: "last_statuses", label: "Últimos status", render: (r) => r.last_statuses.join(" → ") },
            { key: "last_date", label: "Última", render: (r) => fmtDay(r.last_date) },
            { key: "trend_label", label: "Tendência" },
          ]} />
        </Card>
        <Card title="Pressão por setor" subtitle="Ordenado por prioridade (score explicável)">
          <Table rows={data?.sector_pressure} empty="Sem inspeções" cols={[
            { key: "area_name", label: "Setor" },
            { key: "normal", label: "Normal", right: true },
            { key: "attention", label: "Atenção", right: true },
            { key: "critical", label: "Crítico", right: true },
            { key: "recurrent", label: "Reinc.", right: true },
            { key: "activity_index", label: "Atividade", right: true, render: (r) => `${r.activity_index}%` },
            { key: "priority_score", label: "Prioridade", right: true, render: (r) => (
              <span title={(r.reasons || []).join("; ")} className="font-medium" style={{ color: RISK_INFO[r.priority_level]?.color }}>
                {r.priority_score ?? "—"}
              </span>) },
          ]} />
        </Card>
        <Card title="Top 10 porta-iscas com maior consumo" className="lg:col-span-2">
          <Table rows={data?.top_consumption} empty="Nenhum consumo no período" cols={[
            { key: "code", label: "Dispositivo", render: (r) => <button className="underline font-mono" onClick={() => setDevice(r.device_id)}>{r.code}</button> },
            { key: "area_name", label: "Área" },
            { key: "events", label: "Eventos", right: true },
            { key: "partial", label: "Parciais", right: true },
            { key: "consumed", label: "Consumidos", right: true },
            { key: "last_activity", label: "Última atividade", render: (r) => fmtDay(r.last_activity) },
            { key: "trend_label", label: "Tendência" },
          ]} />
        </Card>
      </div>

      {/* Transições */}
      <Card title="Transições de status" subtitle="Comparação de cada inspeção com a anterior do mesmo dispositivo (clique para detalhar)">
        {!data?.transitions?.series?.some((s) => s.PIORA || s.ESTAVEL || s.MELHORA) ? <Empty /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.transitions.series} margin={{ left: -16, right: 12, top: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="bucket" tickFormatter={fmtDay} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} labelFormatter={fmtDay} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {["PIORA", "ESTAVEL", "MELHORA"].map((d, i) => (
                <Bar key={d} dataKey={d} name={`${TRANSITION_INFO[d].label} (${data.transitions.totals[d]})`} stackId="t"
                  fill={TRANSITION_INFO[d].color} {...GAP} maxBarSize={28} radius={i === 2 ? [4, 4, 0, 0] : 0}
                  className="cursor-pointer" onClick={(evt) => { const row = evt?.payload || evt; return setDrill({ title: `${TRANSITION_INFO[d].label} — ${fmtDay(row.bucket)}`,
                    params: { metric: "transition", transition: d, bucket: row.bucket } }); }} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* LINHA 5 — T-Rex + Luminosa */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="T-Rex" subtitle="Unidade: roedores capturados">
          {!data?.trex?.has_data ? <Empty text="Sem T-Rex inspecionadas no período" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Monitoradas" value={data.trex.monitored_devices} />
                <Stat label="Sem captura" value={data.trex.no_capture} accent={STATUS.good} />
                <Stat label="Com captura" value={data.trex.with_capture} hint={`${data.trex.devices_with_capture} dispositivo(s)`} />
                <Stat label="Roedores" value={data.trex.total_rodents} hint={<Delta cmp={data.trex.comparison} />} />
                <Stat label="Média/dispositivo" value={data.trex.mean_per_device} />
                <Stat label="Reincidentes" value={data.trex.recurrent} />
              </div>
              <div className="text-xs font-medium">Captura por espécie</div>
              {!data.trex.by_species.length ? <Empty text="Sem capturas" /> : (
                <div className="space-y-1">
                  {data.trex.by_species.map((s) => (
                    <div key={s.species_id} className="flex items-center gap-2 text-sm">
                      <span aria-hidden style={{ color: speciesColor[s.species_id] }}>●</span>
                      <span>{s.name}</span><span className="ml-auto font-medium">{s.total}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.trex.by_sector.length > 0 && (
                <>
                  <div className="text-xs font-medium">Captura por setor</div>
                  <HBar data={data.trex.by_sector} dataKey="rodents" nameKey="area_name" unit="roedores"
                    color={speciesColor[data.trex.by_species[0]?.species_id] || "#2a78d6"} />
                </>
              )}
            </div>
          )}
        </Card>
        <Card title="Armadilhas luminosas" subtitle="Unidade: insetos (somente resultados oficiais; IA só após validação)">
          {!data?.luminous?.has_data ? <Empty text="Sem luminosas inspecionadas no período" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Monitoradas" value={data.luminous.monitored_devices} />
                <Stat label="Total de insetos" value={data.luminous.total} hint={<Delta cmp={data.luminous.comparison} />} />
                <Stat label="Média/luminosa" value={data.luminous.mean_per_device} />
                <Stat label="Maior captura" value={data.luminous.max.value} hint={data.luminous.max.code} />
                <Stat label="Predominante" value={data.luminous.predominant_species?.name || "—"}
                  hint={data.luminous.predominant_species ? `${data.luminous.predominant_species.total} insetos` : undefined} />
              </div>
              <div className="text-xs font-medium">Captura por espécie</div>
              <div className="flex h-3 rounded overflow-hidden gap-0.5">
                {data.luminous.by_species.map((s) => (
                  <div key={s.species_id} title={`${s.name}: ${s.total}`}
                    style={{ width: `${(s.total / data.luminous.total) * 100}%`, background: speciesColor[s.species_id] }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {data.luminous.by_species.map((s) => (
                  <span key={s.species_id} className="flex items-center gap-1">
                    <span aria-hidden style={{ color: speciesColor[s.species_id] }}>●</span>{s.name}: <b>{s.total}</b>
                  </span>
                ))}
              </div>
              <div className="text-xs font-medium">Top luminosas</div>
              <HBar data={data.luminous.by_device} dataKey="value" nameKey="code" unit="insetos" color="#2a78d6"
                onClick={(evt) => { const row = evt?.payload || evt; return setDevice(row.device_id); }} />
            </div>
          )}
        </Card>
      </div>

      {/* LINHA 6 — Biológica + Insights/Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Armadilhas biológicas" subtitle="Unidade: gramas (peso capturado)">
          {!data?.biological?.has_data ? <Empty text="Sem biológicas inspecionadas no período" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Monitoradas" value={data.biological.monitored_devices} />
                <Stat label="Peso total" value={`${data.biological.total} g`} hint={<Delta cmp={data.biological.comparison} unit=" g" />} />
                <Stat label="Peso médio" value={`${data.biological.mean_per_device} g`} />
                <Stat label="Maior peso" value={`${data.biological.max.value} g`} hint={data.biological.max.code} />
              </div>
              <div className="text-xs font-medium">Peso por setor</div>
              <HBar data={data.biological.by_sector} dataKey="value" nameKey="area_name" unit="g" color="#eb6834" />
              <div className="text-xs font-medium">Top dispositivos (g)</div>
              <HBar data={data.biological.by_device} dataKey="value" nameKey="code" unit="g" color="#eb6834"
                onClick={(evt) => { const row = evt?.payload || evt; return setDevice(row.device_id); }} />
            </div>
          )}
        </Card>
        <Card title="Insights e alertas" subtitle="Gerados somente a partir de dados calculados">
          {!insights.length && !alerts.length ? <Empty text="Nenhum insight ou alerta aberto" /> : (
            <ul className="space-y-2 text-sm">
              {insights.slice(0, 5).map((i, idx) => (
                <li key={`i${idx}`} className="rounded-md border border-border p-2">
                  <div className="font-medium">{i.insight}</div>
                  <div className="text-xs text-muted-foreground">{i.data}</div>
                </li>
              ))}
              {alerts.map((a) => (
                <li key={a.id} className="flex items-start gap-2 rounded-md border border-border p-2">
                  <span aria-hidden style={{ color: a.severity === "alta" ? STATUS.critical : STATUS.warning }}>▲</span>
                  <div>
                    <div className="font-medium">{a.label}{a.device_code ? ` — ${a.device_code}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{a.message}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {drill && <DrillDown request={drill} filters={filters} onClose={closeDrill} onOpenDevice={openDevice} />}
      {device && <DeviceDetail deviceId={device} onClose={closeDevice} />}
      <p className="text-xs text-muted-foreground">
        Período {fmtDay(data?.period?.from)} – {fmtDay(data?.period?.to)} · comparação com {fmtDay(data?.period?.prev_from)} –{" "}
        {fmtDay(data?.period?.prev_to)} · tipos: {typeLabel(filters.type_key) === "—" ? "todos" : typeLabel(filters.type_key)}
      </p>
    </div>
  );
}

